import React, { useCallback, useEffect, useState } from "react";

import styled from "styled-components";
import { Modal, Typography, Statistic, Select, Form, Spin, Col, Row } from "antd";
import { debounce } from "lodash";

import { getDrugsNameAPI, getPapDrugCostCalculation, putDrugCostCalculationAPI } from "../../api/api";
import { useFetch } from "../../hooks/fetch";
import { useTranslation } from "react-i18next";
import { SingleSelectWithFloatLabel } from "../customComponent/CustomSelect";
import { CustomInputNumber } from "../customComponent/CustomInputNumber";
import { sizes } from "../../constant/styles";
import { DARK_GRAY } from "../../constant/colors";
import { jsonToFormData, parseDecimals } from "../../utils/formaters";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";

const { Text } = Typography;
const { Option } = Select;

const formatNDC11 = (ndc11) =>
  ndc11 ? ndc11.substring(0, 5) + "-" + ndc11.substring(5, 9) + "-" + ndc11.substring(9) : null;

const StyledModal = styled(Modal)`
  div.ant-modal-content > div.ant-modal-header {
    background: #2854a1;
  }
  div.ant-modal-content > div.ant-modal-header > div.ant-modal-title,
  div.ant-modal-content > button.ant-modal-close > span.ant-modal-close-x {
    color: #fefeff;
  }
`;
const DrugPriceCalculatorModal = ({ papId, onConfirm, visible, setVisible, clearLocalData, shouldUpdate }) => {
  const [productName, setProductName] = useState(null);
  const [products, setProducts] = useState({});
  const [marketerName, setMarketerName] = useState(null);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(null);
  const [numberUnits, setNumberUnits] = useState("");
  const [totalPrice, setTotalPrice] = useState(null);
  const [disabledSave, setDisabledSave] = useState(false);

  const [form] = Form.useForm();

  const { t } = useTranslation();

  const clearFields = () => {
    setProductName(null);
    setProducts(null);
    setMarketerName(null);
    setSelectedPackageIndex(0);
    setNumberUnits(null);
    setTotalPrice(null);
  };
  const [getDrugsData, getDrugsDataResult, getDrugsDataIsFetching, getDrugsDataNameError] =
    useFetch(getPapDrugCostCalculation);
  const [getDrugsName, getDrugsNameResult, getDrugsNameIsFetching, getDrugsNameNameError, clearDrugsNameResult] =
    useFetch(getDrugsNameAPI);

  const [updateDrugCostCalculation, , isUpdatingDrugCostCalculation, updateDrugCostCalculationErrors] =
    useFetch(putDrugCostCalculationAPI);

  useEffect(() => {
    getDrugsData(papId);
  }, [papId]);

  useEffect(() => {
    if (clearLocalData) {
      clearFields();
    } else if (getDrugsDataResult?.data) {
      const { marketerName, productName, estimatedCost, numberOfPackages, productPackageSizes } =
        getDrugsDataResult.data;
      setProductName(productName);
      setProducts(productPackageSizes);
      setMarketerName(marketerName);
      setSelectedPackageIndex(0);
      setNumberUnits(numberOfPackages);
      setTotalPrice(estimatedCost);
    }
  }, [getDrugsDataResult, clearLocalData]);

  useEffect(() => {
    if (updateDrugCostCalculationErrors) ErrorMessage(t("errorMessages.failed_to_update_drugs_calculations"));
  }, [updateDrugCostCalculationErrors]);

  useEffect(() => {
    if (getDrugsNameNameError || getDrugsDataNameError) {
      ErrorMessage(t("errorMessages.failed_to_get_drugs"));
    }
  }, [getDrugsNameNameError, getDrugsDataNameError]);

  useEffect(() => {
    if (marketerName && Number.isInteger(selectedPackageIndex) && numberUnits) {
      setTotalPrice(parseDecimals(products[marketerName][selectedPackageIndex].pricePerPackage * numberUnits, 2));
    }
  }, [selectedPackageIndex, numberUnits]);

  const handleDrugSearch = (drugName) => {
    if (!drugName) return;
    setProductName(drugName);

    if (drugName.length < 3) return;
    debouncedSearch(drugName);
  };

  const debouncedSearch = useCallback(
    debounce((drugName) => getDrugsName(papId, drugName), 500),
    []
  );

  const onOk = async () => {
    if (!marketerName || isNaN(selectedPackageIndex)) return;
    const drugPackage = products[marketerName][selectedPackageIndex];
    const { packageSize, ndc11, quantity, packageId, pricePerPackage } = drugPackage;
    const newValues = {
      manufacturer: marketerName,
      drug: {
        productName,
        packageSizes: products
      },
      drugPackage: {
        ...drugPackage,
        formattedNdc11: formatNDC11(ndc11),
        sizeAndQuantity: `${packageSize}/${quantity}`
      },
      numberOfPackages: numberUnits,
      estimatedCost: totalPrice,
      packageId,
      pricePerPackage,
      totalPrice
    };

    if (shouldUpdate) {
      await updateDrugCostCalculation(papId, jsonToFormData(newValues));
      onConfirm({ quantity: parseDecimals(numberUnits, 2), unitCost: pricePerPackage, totalAmount: totalPrice });
    }

    setVisible(false);
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setDisabledSave(hasErrors);
  };
  return (
    <StyledModal
      open={visible}
      okButtonProps={{
        id: "drug-calculator-ok-button",
        disabled: !totalPrice || isUpdatingDrugCostCalculation || disabledSave || numberUnits < 1
      }}
      cancelButtonProps={{ id: "drug-calculator-cancel-button" }}
      confirmLoading={isUpdatingDrugCostCalculation}
      title={t("calculator.title")}
      centered
      onOk={onOk}
      onCancel={() => {
        setVisible(false);
      }}
      okText={t("apply")}
    >
      {getDrugsDataIsFetching && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200
          }}
        >
          <Spin />
          <Text>{t("calculator.loading_drug_information")}</Text>
        </div>
      )}
      {!getDrugsDataIsFetching && (
        <Form form={form} onFieldsChange={handleFormChange} style={{ minHeight: 200 }}>
          <Form.Item>
            <SingleSelectWithFloatLabel
              value={productName}
              label={t("calculator.drugNameTitle")}
              showSearch={true}
              required={true}
              onSearch={handleDrugSearch}
              loading={getDrugsNameIsFetching}
              notFoundContent={
                getDrugsNameIsFetching ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Spin />
                  </div>
                ) : (
                  <Text>{t("drugs.add_drug_form.form.drug_name.search")}</Text>
                )
              }
              onSelect={(value) => {
                if (!value) {
                  setProductName(null);
                  clearDrugsNameResult();
                } else {
                  setProductName(value);
                  const manufacturersNames = Object.keys(getDrugsNameResult.data[value]);
                  if (manufacturersNames.length === 1) {
                    const manufacturerName = manufacturersNames[0];
                    setMarketerName(manufacturerName);
                    if (getDrugsNameResult.data[value][manufacturerName].length === 1) {
                      setSelectedPackageIndex(0);
                    } else {
                      setSelectedPackageIndex(null);
                    }
                  } else {
                    setMarketerName(null);
                  }
                  setProducts(getDrugsNameResult.data[value]);
                  setTotalPrice(null);
                  setNumberUnits(null);
                }
              }}
            >
              {getDrugsNameResult?.data &&
                Object.keys(getDrugsNameResult.data).map((drug, index) => (
                  <Option key={index} value={drug}>
                    {drug}
                  </Option>
                ))}
            </SingleSelectWithFloatLabel>
            {marketerName && Number.isInteger(selectedPackageIndex) && (
              <Text style={{ fontSize: sizes.small, color: DARK_GRAY }}>{`${formatNDC11(
                products[marketerName][selectedPackageIndex].ndc11
              )} / ${products[marketerName][selectedPackageIndex].description}`}</Text>
            )}
          </Form.Item>
          {products && (
            <>
              <Form.Item>
                <SingleSelectWithFloatLabel
                  value={marketerName}
                  label={t("calculator.manufacturerTitle")}
                  required={true}
                  onSelect={(value) => {
                    setMarketerName(value);
                    if (products[value].length === 1) {
                      setSelectedPackageIndex(0);
                    }
                  }}
                >
                  {Object.keys(products).map((manufacturer, index) => (
                    <Option key={index} value={manufacturer}>
                      {manufacturer}
                    </Option>
                  ))}
                </SingleSelectWithFloatLabel>
              </Form.Item>
              {marketerName && (
                <Row gutter={16} style={{ display: "flex", justifyContent: "space-between" }}>
                  <Col span={12}>
                    <Form.Item>
                      <SingleSelectWithFloatLabel
                        value={
                          Number.isInteger(selectedPackageIndex)
                            ? `${products[marketerName][selectedPackageIndex].quantity}/${products[marketerName][selectedPackageIndex].packageSize}`
                            : null
                        }
                        label={t("calculator.unitSizeTitle")}
                        onSelect={(value) => setSelectedPackageIndex(value)}
                      >
                        {products[marketerName].map((_, index) => (
                          <Option key={index} value={index}>
                            {`${products[marketerName][index].quantity}/${products[marketerName][index].packageSize}`}
                          </Option>
                        ))}
                      </SingleSelectWithFloatLabel>
                      {Number.isInteger(selectedPackageIndex) && (
                        <Text style={{ fontSize: sizes.small, color: DARK_GRAY }}>{`$${parseDecimals(
                          products[marketerName][selectedPackageIndex].pricePerPackage,
                          2
                        )} ${t("calculator.perUnit")}`}</Text>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <CustomInputNumber
                      name={"numberUnits"}
                      value={numberUnits}
                      label={t("calculator.unitNumberTitle")}
                      onChange={(e) => setNumberUnits(e?.target?.value || "")}
                      rules={[{ required: false }]}
                    />
                  </Col>
                </Row>
              )}
              {totalPrice && <Statistic title={t("calculator.totalDrugPrice")} value={totalPrice} />}
            </>
          )}
        </Form>
      )}
    </StyledModal>
  );
};

export { DrugPriceCalculatorModal };
